import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import '../../services/GlobalVariables'

const API_URL = global.SET_API_URL + 'production/'

const initialState = {
    items: [],
    formulas: [],
    selectedFormula: null,
    batches: [],
    selectedBatch: null,
    ledger: [],
    itemsWithStock: [],
    issuances: [],
    departments: [],
    isLoading: false,
    isError: false,
    isSuccess: false,
    message: ''
}

export const getDepartments = createAsyncThunk('production/departments', async (_, thunkAPI) => {
    try {
        const result = await axios.get(API_URL + 'departments')
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

export const getProductionItems = createAsyncThunk('production/items', async (_, thunkAPI) => {
    try {
        const result = await axios.get(API_URL + 'items')
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

export const getFormulas = createAsyncThunk('production/formulas', async (_, thunkAPI) => {
    try {
        const result = await axios.get(API_URL + 'formulas')
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

export const getFormulaByID = createAsyncThunk('production/formulaByID', async (formulaID, thunkAPI) => {
    try {
        const result = await axios.get(API_URL + 'formulas/' + formulaID)
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

export const saveFormula = createAsyncThunk('production/saveFormula', async (payload, thunkAPI) => {
    try {
        const result = await axios.post(API_URL + 'formulas', payload)
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

export const getBatches = createAsyncThunk('production/batches', async (_, thunkAPI) => {
    try {
        const result = await axios.get(API_URL + 'batches')
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

export const getBatchByID = createAsyncThunk('production/batchByID', async (batchID, thunkAPI) => {
    try {
        const result = await axios.get(API_URL + 'batches/' + batchID)
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

export const createBatch = createAsyncThunk('production/createBatch', async (payload, thunkAPI) => {
    try {
        const result = await axios.post(API_URL + 'batches', payload)
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

export const getStockLedger = createAsyncThunk('production/ledger', async (params, thunkAPI) => {
    try {
        const result = await axios.get(API_URL + 'ledger', { params })
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

export const getItemsWithStock = createAsyncThunk('production/itemsWithStock', async (params, thunkAPI) => {
    try {
        const result = await axios.get(API_URL + 'issuance/items', { params })
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

export const getIssuances = createAsyncThunk('production/issuances', async (params, thunkAPI) => {
    try {
        const result = await axios.get(API_URL + 'issuance', { params })
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

export const saveManualIssuance = createAsyncThunk('production/saveIssuance', async (payload, thunkAPI) => {
    try {
        const result = await axios.post(API_URL + 'issuance', payload)
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && (error.response.data.error || error.response.data.message)) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

export const updateManualIssuance = createAsyncThunk('production/updateIssuance', async ({ issuanceID, ...payload }, thunkAPI) => {
    try {
        const result = await axios.put(API_URL + 'issuance/' + issuanceID, payload)
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && (error.response.data.error || error.response.data.message)) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

const productionSlice = createSlice({
    name: 'production',
    initialState,
    reducers: {
        resetProductionState: (state) => {
            state.isLoading = false
            state.isError = false
            state.isSuccess = false
            state.message = ''
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getProductionItems.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getProductionItems.fulfilled, (state, action) => {
                state.isLoading = false
                state.items = action.payload
            })
            .addCase(getProductionItems.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })

            .addCase(getFormulas.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getFormulas.fulfilled, (state, action) => {
                state.isLoading = false
                state.formulas = action.payload
            })
            .addCase(getFormulas.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })

            .addCase(getFormulaByID.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getFormulaByID.fulfilled, (state, action) => {
                state.isLoading = false
                state.selectedFormula = action.payload
            })
            .addCase(getFormulaByID.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })

            .addCase(saveFormula.pending, (state) => {
                state.isLoading = true
            })
            .addCase(saveFormula.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.selectedFormula = action.payload.data
            })
            .addCase(saveFormula.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })

            .addCase(getBatches.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getBatches.fulfilled, (state, action) => {
                state.isLoading = false
                state.batches = action.payload
            })
            .addCase(getBatches.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })

            .addCase(getBatchByID.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getBatchByID.fulfilled, (state, action) => {
                state.isLoading = false
                state.selectedBatch = action.payload
            })
            .addCase(getBatchByID.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })

            .addCase(createBatch.pending, (state) => {
                state.isLoading = true
            })
            .addCase(createBatch.fulfilled, (state) => {
                state.isLoading = false
                state.isSuccess = true
            })
            .addCase(createBatch.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })

            .addCase(getStockLedger.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getStockLedger.fulfilled, (state, action) => {
                state.isLoading = false
                state.ledger = action.payload
            })
            .addCase(getStockLedger.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })

            .addCase(getItemsWithStock.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getItemsWithStock.fulfilled, (state, action) => {
                state.isLoading = false
                state.itemsWithStock = action.payload
            })
            .addCase(getItemsWithStock.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })

            .addCase(getIssuances.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getIssuances.fulfilled, (state, action) => {
                state.isLoading = false
                state.issuances = action.payload
            })
            .addCase(getIssuances.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })

            .addCase(saveManualIssuance.pending, (state) => {
                state.isLoading = true
            })
            .addCase(saveManualIssuance.fulfilled, (state) => {
                state.isLoading = false
                state.isSuccess = true
            })
            .addCase(saveManualIssuance.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })
            .addCase(updateManualIssuance.pending, (state) => {
                state.isLoading = true
            })
            .addCase(updateManualIssuance.fulfilled, (state) => {
                state.isLoading = false
                state.isSuccess = true
            })
            .addCase(updateManualIssuance.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })

            .addCase(getDepartments.fulfilled, (state, action) => {
                state.departments = action.payload
            })
    }
})

export const { resetProductionState } = productionSlice.actions
export default productionSlice.reducer
